import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { ArrowLeft } from "lucide-react";
import { ConsentModal } from "@/components/ConsentModal";

export default function Auth() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [pendingCredentials, setPendingCredentials] = useState<{ email: string; password: string } | null>(null);
  const [passwordError, setPasswordError] = useState<string>("");

  useEffect(() => {
    if (user) {
      navigate("/home");
    }
  }, [user, navigate]);

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    if (password.length < 12) {
      setPasswordError("Le mot de passe doit contenir au moins 12 caractères.");
      return;
    }

    setPasswordError("");
    setPendingCredentials({ email, password });
    setShowConsent(true);
  };

  const handleConsentAccept = async () => {
    if (!pendingCredentials) return;

    setLoading(true);
    try {
      const { email, password } = pendingCredentials;

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (error) throw error;

      if (data.user) {
        await supabase.from("consents").insert({
          user_id: data.user.id,
          consent_type: "terms_and_privacy",
          consent_version: "1.0",
          consent_given: true,
          ip_address: null,
          user_agent: navigator.userAgent,
        });
      }

      toast.success("Compte créé avec succès ! Bienvenue 🎉");
      navigate("/home");
    } catch (error: any) {
      toast.error(error.message || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
      setShowConsent(false);
      setPendingCredentials(null);
    }
  };

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      toast.success("Connexion réussie !");
      navigate("/home");
    } catch (error: any) {
      toast.error(error.message || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-dvh flex items-center justify-center bg-background p-4">
      <ConsentModal open={showConsent} onAccept={handleConsentAccept} />

      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="self-start">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <div className="flex justify-center">
            <Logo />
          </div>
          <CardTitle className="text-2xl text-center">Bienvenue</CardTitle>
          <CardDescription className="text-center">
            Connecte-toi ou crée un compte pour continuer
          </CardDescription>
        </CardHeader>

        <CardContent>
          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">Connexion</TabsTrigger>
              <TabsTrigger value="signup">Inscription</TabsTrigger>
            </TabsList>

            <TabsContent value="signin" className="space-y-4">
              <form onSubmit={handleSignIn} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signin-email">Email</Label>
                  <Input id="signin-email" name="email" type="email" placeholder="ton@email.com" required />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="signin-password">Mot de passe</Label>
                    <Button type="button" variant="link" size="sm" onClick={() => navigate("/reset-password")} className="p-0 h-auto">
                      Oublié ?
                    </Button>
                  </div>
                  <Input id="signin-password" name="password" type="password" required />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Connexion..." : "Se connecter"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup" className="space-y-4">
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input id="signup-email" name="email" type="email" placeholder="ton@email.com" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Mot de passe</Label>
                  <Input id="signup-password" name="password" type="password" minLength={12} required onChange={() => setPasswordError("")} />
                  {passwordError ? (
                    <p className="text-xs text-destructive font-medium">{passwordError}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">Minimum 12 caractères</p>
                  )}
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Création..." : "Créer mon compte"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>

        <div className="px-6 pb-6">
          <div className="text-xs text-center text-muted-foreground">
            En continuant, tu acceptes nos{" "}
            <Button variant="link" size="sm" onClick={() => navigate("/legal/terms")} className="p-0 h-auto text-xs">CGU</Button>
            {" "}et notre{" "}
            <Button variant="link" size="sm" onClick={() => navigate("/legal/privacy")} className="p-0 h-auto text-xs">Politique de confidentialité</Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
